import React, { useState, useEffect, useCallback } from 'react';
import { cases, teams, invitations, users } from '../../services/api';

function TeamManager({ caseId, onClose }) {
  const [teamList, setTeamList] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Team creation
  const [newTeamName, setNewTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);

  // Expanded team for member management
  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [teamInvitations, setTeamInvitations] = useState({});

  // Add member state
  const [selectedUserId, setSelectedUserId] = useState('');
  const [addingMember, setAddingMember] = useState(false);

  // Invitation creation state per team
  const [creatingInvitation, setCreatingInvitation] = useState(null);
  const [invitationSettings, setInvitationSettings] = useState({
    maxUses: '',
    expiresAt: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [teamsRes, usersRes] = await Promise.all([
        cases.getTeams(caseId),
        users.getAll({ role: 'analyst', limit: 200 })
      ]);

      setTeamList(teamsRes.data);
      setAvailableUsers(usersRes.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error carregant dades');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load invitations for a team when expanded
  const loadTeamInvitations = async (teamId) => {
    try {
      const { data } = await invitations.getAll({ teamId });
      setTeamInvitations(prev => ({ ...prev, [teamId]: data }));
    } catch (err) {
      console.error('Error loading team invitations:', err);
    }
  };

  const handleExpandTeam = (teamId) => {
    if (expandedTeamId === teamId) {
      setExpandedTeamId(null);
    } else {
      setExpandedTeamId(teamId);
      if (!teamInvitations[teamId]) {
        loadTeamInvitations(teamId);
      }
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    try {
      setCreatingTeam(true);
      setError('');
      await cases.createTeam(caseId, { name: newTeamName.trim() });
      setNewTeamName('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error creant equip');
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    const team = teamList.find(t => t.id === teamId);
    if (!window.confirm(`Segur que vols eliminar l'equip "${team?.name}"? Aquesta acció no es pot desfer.`)) {
      return;
    }

    try {
      setError('');
      await teams.delete(teamId);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant equip');
    }
  };

  const handleAddMember = async (teamId) => {
    if (!selectedUserId) return;

    try {
      setAddingMember(true);
      setError('');
      await teams.addMember(teamId, { userId: selectedUserId });
      setSelectedUserId('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error afegint membre');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (teamId, userId, userName) => {
    if (!window.confirm(`Segur que vols treure ${userName} de l'equip?`)) {
      return;
    }

    try {
      setError('');
      await teams.removeMember(teamId, userId);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant membre');
    }
  };

  const handleCreateInvitation = async (teamId) => {
    try {
      setCreatingInvitation(teamId);
      setError('');

      await invitations.create({
        teamId,
        maxUses: invitationSettings.maxUses ? parseInt(invitationSettings.maxUses) : null,
        expiresAt: invitationSettings.expiresAt || null
      });

      setInvitationSettings({ maxUses: '', expiresAt: '' });
      await loadTeamInvitations(teamId);
    } catch (err) {
      setError(err.response?.data?.message || 'Error creant codi d\'invitació');
    } finally {
      setCreatingInvitation(null);
    }
  };

  const handleDeactivateInvitation = async (invitationId, teamId) => {
    try {
      setError('');
      await invitations.deactivate(invitationId);
      await loadTeamInvitations(teamId);
    } catch (err) {
      setError(err.response?.data?.message || 'Error desactivant invitació');
    }
  };

  const handleDeleteInvitation = async (invitationId, teamId) => {
    if (!window.confirm('Segur que vols eliminar aquest codi?')) return;

    try {
      setError('');
      await invitations.delete(invitationId);
      await loadTeamInvitations(teamId);
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant invitació');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Get users not in any team for this case
  const getAvailableUsersForTeam = (teamId) => {
    const allTeamMemberIds = teamList.flatMap(t => t.members?.map(m => m.userId) || []);
    return availableUsers.filter(u => !allTeamMemberIds.includes(u.id));
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-surface rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold text-content">Gestionar Equips</h2>
          <button onClick={onClose} className="text-content-muted hover:text-content-secondary">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Create Team Form */}
          <div className="bg-surface-subtle p-4 rounded-lg mb-4">
            <h3 className="font-medium mb-3 text-content">Crear nou equip</h3>
            <form onSubmit={handleCreateTeam} className="flex space-x-3">
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Nom de l'equip..."
                className="input flex-1"
              />
              <button
                type="submit"
                disabled={creatingTeam || !newTeamName.trim()}
                className="btn btn-primary disabled:opacity-50"
              >
                {creatingTeam ? 'Creant...' : 'Crear equip'}
              </button>
            </form>
          </div>

          {/* Teams List */}
          <div className="space-y-3">
            {teamList.length === 0 ? (
              <p className="text-content-muted text-center py-8">
                Encara no hi ha equips. Crea el primer equip per continuar.
              </p>
            ) : (
              teamList.map(team => (
                <div key={team.id} className="border border-border rounded-lg overflow-hidden">
                  {/* Team Header */}
                  <div
                    className="p-4 bg-surface-subtle flex items-center justify-between cursor-pointer hover:bg-surface-muted"
                    onClick={() => handleExpandTeam(team.id)}
                  >
                    <div className="flex items-center space-x-3">
                      <svg
                        className={`w-5 h-5 text-content-muted transition-transform ${expandedTeamId === team.id ? 'rotate-90' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <div>
                        <span className="font-medium text-lg text-content">{team.name}</span>
                        <span className="text-content-muted ml-2">
                          ({team.members?.length || 0} membres)
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTeam(team.id);
                        }}
                        className="text-red-600 hover:text-error-text text-sm px-2 py-1"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>

                  {/* Expanded Team Content */}
                  {expandedTeamId === team.id && (
                    <div className="p-4 border-t border-border space-y-4 bg-surface">
                      {/* Members Section */}
                      <div>
                        <h4 className="font-medium text-content-secondary mb-2">Membres de l'equip</h4>

                        {/* Current Members */}
                        {team.members && team.members.length > 0 ? (
                          <div className="space-y-2 mb-3">
                            {team.members.map(member => (
                              <div key={member.userId} className="flex items-center justify-between p-2 bg-surface-subtle rounded">
                                <div>
                                  <span className="font-medium text-content">{member.email}</span>
                                  {member.firstName && (
                                    <span className="text-content-muted ml-2">
                                      ({member.firstName} {member.lastName})
                                    </span>
                                  )}
                                  <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                                    member.role === 'leader' ? 'bg-warning-subtle text-warning-text' : 'bg-surface-muted text-content-secondary'
                                  }`}>
                                    {member.role}
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleRemoveMember(team.id, member.userId, member.email)}
                                  className="text-red-600 hover:text-error-text text-sm"
                                >
                                  Treure
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-content-muted text-sm mb-3">Aquest equip no té membres</p>
                        )}

                        {/* Add Member Form */}
                        <div className="flex space-x-2">
                          <select
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            className="input flex-1"
                          >
                            <option value="">Selecciona un usuari per afegir...</option>
                            {getAvailableUsersForTeam(team.id).map(user => (
                              <option key={user.id} value={user.id}>
                                {user.email} {user.firstName && `(${user.firstName} ${user.lastName})`}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleAddMember(team.id)}
                            disabled={addingMember || !selectedUserId}
                            className="btn btn-secondary disabled:opacity-50"
                          >
                            {addingMember ? '...' : 'Afegir'}
                          </button>
                        </div>
                      </div>

                      {/* Invitation Codes Section */}
                      <div className="border-t border-border pt-4">
                        <h4 className="font-medium text-content-secondary mb-2">Codis d'invitació per a aquest equip</h4>
                        <p className="text-sm text-content-muted mb-3">
                          Els usuaris poden unir-se a aquest equip amb un codi d'invitació
                        </p>

                        {/* Existing Codes */}
                        {teamInvitations[team.id] && teamInvitations[team.id].length > 0 && (
                          <div className="space-y-2 mb-3">
                            {teamInvitations[team.id].map(inv => (
                              <div
                                key={inv.id}
                                className={`p-3 border border-border rounded-lg ${!inv.isActive ? 'bg-surface-muted opacity-60' : 'bg-surface'}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <code className="bg-surface-muted text-content px-3 py-1 rounded font-mono text-lg">
                                      {inv.code}
                                    </code>
                                    <button
                                      onClick={() => copyToClipboard(inv.code)}
                                      className="text-content-muted hover:text-content-secondary"
                                      title="Copiar codi"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                  </div>
                                  <div className="flex space-x-2">
                                    {inv.isActive && (
                                      <button
                                        onClick={() => handleDeactivateInvitation(inv.id, team.id)}
                                        className="text-yellow-600 hover:text-warning-text text-sm"
                                      >
                                        Desactivar
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleDeleteInvitation(inv.id, team.id)}
                                      className="text-red-600 hover:text-error-text text-sm"
                                    >
                                      Eliminar
                                    </button>
                                  </div>
                                </div>
                                <div className="mt-2 text-sm text-content-muted flex items-center space-x-4">
                                  <span>
                                    Usos: {inv.usesCount}{inv.maxUses ? ` / ${inv.maxUses}` : ' (il·limitat)'}
                                  </span>
                                  {inv.expiresAt && (
                                    <span>
                                      Expira: {new Date(inv.expiresAt).toLocaleString()}
                                    </span>
                                  )}
                                  {!inv.isActive && (
                                    <span className="text-error">Desactivat</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Create Invitation Form */}
                        <div className="bg-surface-subtle p-3 rounded-lg">
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-sm text-content-secondary mb-1">Màxim d'usos (opcional)</label>
                              <input
                                type="number"
                                min="1"
                                placeholder="Il·limitat"
                                value={invitationSettings.maxUses}
                                onChange={(e) => setInvitationSettings(prev => ({ ...prev, maxUses: e.target.value }))}
                                className="input w-full"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-content-secondary mb-1">Expira (opcional)</label>
                              <input
                                type="datetime-local"
                                value={invitationSettings.expiresAt}
                                onChange={(e) => setInvitationSettings(prev => ({ ...prev, expiresAt: e.target.value }))}
                                className="input w-full"
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => handleCreateInvitation(team.id)}
                            disabled={creatingInvitation === team.id}
                            className="btn btn-primary disabled:opacity-50"
                          >
                            {creatingInvitation === team.id ? 'Creant...' : 'Crear codi d\'invitació'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">
            Tancar
          </button>
        </div>
      </div>
    </div>
  );
}

export default TeamManager;
